'use client'

import {
  FormRadio,
  FormRadioGroup,
  RadioGroup,
  RadioProvider,
  useFormContext,
} from '@ariakit/react'
import { Fragment, useCallback, useContext, useRef, useState } from 'react'
import { useUpdateEffect } from 'react-use'
import styled from 'styled-components'

import { ColorSwatch, TypeStyle, fromMd, untilMd } from '@syconium/little-miss-figgy'

import ColorGalleryCta from '../../../brunswick/components/fit-model/ColorGalleryModal/ColorGalleryCta'
import { useColorBadge } from '../../_hooks/useColorBadge'
import { useColorGroupLabel } from '../../_hooks/useColorGroupLabel'
import { useStaticAmplitudeTracking } from '../../_hooks/useStaticTracking'
import { useTranslation } from '../../_providers/TranslationProvider.client'
import {
  groupColorOptionsByColorGroupAndPricing,
  groupColorOptionsBySalePrice,
} from '../../_utils/groupColorOptionsByColorGroup'
import { FormError } from '../form/FormError'
import { PricingRange } from '../pricing/PricingRange.client'

import {
  VariantSelectionFieldProps,
  VariantSelectionFieldValueDisplay,
  VariantSelectionFormContext,
  VariantSelectionFormGroupLabel,
  VariantSelectionFormGroupLabelPrimaryText,
  VariantSelectionFormState,
  VariantSelectionInvisibleInput,
  VariantSelectionOptionsRow,
} from './VariantSelectionForm'

type VariantSelectionColorHeaderProps = {
  onVariantSelectColor?: ((colorKey: string) => void) | undefined
  noBottomMarginTitle?: boolean
}

/**
 * The color attribute's title header, showing the "Color" label, the selected
 * color's display value, and the color gallery CTA.
 */
export const VariantSelectionColorHeader = ({
  onVariantSelectColor,
  noBottomMarginTitle = false,
}: VariantSelectionColorHeaderProps = {}) => {
  const { product, variantGroup } = useContext(VariantSelectionFormContext)!
  const translations = useTranslation()
  const isSingleColor = (product.options.colors ?? []).length <= 1

  return (
    <ColorGroupDisplayTitle $noBottomMargin={noBottomMarginTitle}>
      <VariantSelectionFormGroupLabel>
        <VariantSelectionFormGroupLabelPrimaryText>
          {translations.detailsSection.colorTitle}
          {variantGroup.color?.displayValue ? ':' : ' '}
        </VariantSelectionFormGroupLabelPrimaryText>
        <VariantSelectionFieldValueDisplay>
          {variantGroup.color?.displayValue}
        </VariantSelectionFieldValueDisplay>
      </VariantSelectionFormGroupLabel>

      {variantGroup.primaryCategory.key !== 'SHOES' && !isSingleColor ? (
        <ColorGalleryCta
          productHandle={product.handle}
          ctaVariant='text'
          onSelectColor={onVariantSelectColor}
        />
      ) : null}
    </ColorGroupDisplayTitle>
  )
}

/*
 * priceBucketing:
 *   enabled - splits a color category into pricing buckets, e.g. all colors that are 25% off, all colors that are 35% off, etc
 *   disabled - does not split the color category by price
 *   compact - splits the colors into standard priced colors and on sale colors, no category groupings
 *
 * showTooltips: true shows the tooltip icon, false hides it
 */
type VariantSelectionColorFieldProps = VariantSelectionFieldProps & {
  priceBucketing?: 'enabled' | 'disabled' | 'compact'
}

export const VariantSelectionColorRadioGroup = ({
  onChange,
  priceBucketing = 'disabled',
}: VariantSelectionColorFieldProps) => {
  const { product, secondaryTrackingLocation } = useContext(VariantSelectionFormContext)!
  const formContext = useFormContext()
  const { trackStaticAmplitudeEvent } = useStaticAmplitudeTracking()
  const translateColorGroupLabel = useColorGroupLabel()
  const getColorBadge = useColorBadge()
  const colorGroups =
    priceBucketing === 'compact'
      ? groupColorOptionsBySalePrice(product.options.colors ?? [])
      : groupColorOptionsByColorGroupAndPricing(product.options.colors ?? [])
  const hasASizeInStock = !!product.options.sizes?.find(size => size.outOfStock === false)

  // Keep a local version of value, not just computed from context. So we can quickly rerender
  // with a checked state without waiting for a context rerender which can include a lot of other
  // components.
  const outerValue = formContext!.useValue<VariantSelectionFormState['color']>('color')
  const [value, _setValue] = useState(outerValue)
  const pendingOuterUpdate = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const setValue = useCallback(
    (newValue: typeof outerValue) => {
      // Update inner value with high priority render.
      _setValue(newValue)

      // Deprioritize any external state changed via onChange prop. Only the latest selection is
      // forwarded, so rapid successive changes cannot leave stale writes queued behind it.
      clearTimeout(pendingOuterUpdate.current)
      pendingOuterUpdate.current = setTimeout(() => {
        formContext!.setValue('color', newValue)
      })
    },
    [formContext]
  )

  useUpdateEffect(() => {
    // Sync internal value to match the new one provided. This must not write back to the form,
    // otherwise two in-flight selections echo each other and loop endlessly.
    _setValue(outerValue)

    // This is on purpose. We trigger an on change like this instead of via the input so that if there are two
    // of these fields in one form, the on change for both fires if either is altered.
    // This allows variant selection fields in nested embroidery modals.
    onChange?.(outerValue ?? '')
  }, [outerValue])

  return (
    <div>
      <RadioProvider>
        <ColorGroupRadioGroup data-testid='color-picker' render={<RadioGroup />}>
          {colorGroups.map(colorGroup => {
            const isSaleGroup = colorGroup.group.id === 'sale'
            const buckets =
              priceBucketing === 'disabled' || priceBucketing === 'compact'
                ? [colorGroup.colorOptions]
                : colorGroup.pricingBuckets

            return (
              <div key={colorGroup.group.id}>
                <StyledFormGroupLabelWrapper>
                  <VariantSelectionFormGroupLabel $spaceBetween>
                    {colorGroups.length > 1 ? (
                      <VariantSelectionFormGroupLabelPrimaryTextColor $isSale={isSaleGroup}>
                        {translateColorGroupLabel(colorGroup.group.id)}
                      </VariantSelectionFormGroupLabelPrimaryTextColor>
                    ) : null}
                  </VariantSelectionFormGroupLabel>
                </StyledFormGroupLabelWrapper>

                {buckets.map((colorOptionsBucket, bucketIndex) => {
                  const key =
                    'id' in colorOptionsBucket ? colorOptionsBucket.id : colorGroup.group.id
                  const colorOptions =
                    'colorOptions' in colorOptionsBucket
                      ? colorOptionsBucket.colorOptions
                      : colorOptionsBucket
                  const bucketPricing =
                    'pricing' in colorOptionsBucket ? colorOptionsBucket.pricing : null
                  return (
                    <Fragment key={key}>
                      {bucketPricing && buckets.length > 1 ? (
                        <SubLabel $withTopMargin={bucketIndex > 0}>
                          <PricingRange {...bucketPricing} renderCompareAtText={() => null} />
                        </SubLabel>
                      ) : null}
                      <ColorGroupSwatchRow>
                        {colorOptions.map(colorOption => {
                          const isSelected = value === colorOption.color.id
                          const disabled = isSelected
                            ? colorOption.outOfStock && !hasASizeInStock
                            : colorOption.outOfStock

                          return (
                            <FormRadio
                              key={colorOption.color.id}
                              name='color'
                              value={colorOption.color.id}
                              required
                              checked={isSelected}
                              onClick={() =>
                                trackStaticAmplitudeEvent({
                                  productHandle: product.handle,
                                  secondaryTrackingLocation,
                                  modifiedElement: 'Color Swatch',
                                  modifiedValue: colorOption.color.id,
                                })
                              }
                              render={htmlInputProps => (
                                <label
                                  htmlFor={htmlInputProps.id}
                                  data-color-availability={disabled ? 'unavailable' : 'available'}
                                >
                                  <VariantSelectionInvisibleInput
                                    {...htmlInputProps}
                                    onChange={event => {
                                      if (event.target.checked) setValue(colorOption.color.id)
                                    }}
                                    aria-label={translateColorGroupLabel(colorOption.color.id)}
                                  />
                                  <ColorSwatch
                                    colorId={colorOption.color.id}
                                    hexCodes={colorOption.color.hexCodes ?? []}
                                    swatchUrl={colorOption.color.swatchImage?.url}
                                    // Make sure the color is in-stock before rendering to avoid flicker
                                    badge={
                                      colorOption.outOfStock
                                        ? null
                                        : getColorBadge(
                                            colorOption.color.badge,
                                            colorOption.color.badgeExpiration
                                          )
                                    }
                                    selected={isSelected}
                                    unavailable={disabled}
                                    aria-hidden='true'
                                    size='large'
                                  />
                                </label>
                              )}
                            />
                          )
                        })}
                      </ColorGroupSwatchRow>
                    </Fragment>
                  )
                })}
              </div>
            )
          })}
        </ColorGroupRadioGroup>
      </RadioProvider>

      <FormError name='color' />
    </div>
  )
}

export type VariantSelectionFromTooltipProps = {
  title: string
  description?: string
}

const ColorGroupRadioGroup = styled(FormRadioGroup)`
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.rowGap.extraSmall};
`

const SubLabel = styled(VariantSelectionFormGroupLabel)<{ $withTopMargin?: boolean }>`
  text-transform: uppercase;
  margin-bottom: ${o => o.theme.spacing(1)};
  display: block;
  margin-bottom: ${o => o.theme.rowGap.extraSmall};
  margin-top: ${o => (o.$withTopMargin ? o.theme.spacing(2) : 0)};

  ${fromMd} {
    font-weight: ${o => o.theme.typography.headline.micro.fromMd.fontWeight};
  }
  ${untilMd} {
    font-weight: ${o => o.theme.typography.headline.micro.untilMd.fontWeight};
  }
`

export const VariantSelectionFormGroupLabelPrimaryTextColor = styled(
  VariantSelectionFormGroupLabelPrimaryText
)`
  ${TypeStyle.css.headlineMicro};
  margin-bottom: ${o => o.theme.rowGap.extraSmall};
`

const ColorGroupDisplayTitle = styled.div<{ $noBottomMargin?: boolean }>`
  ${TypeStyle.css.headlineExtraExtraSmall}
  display: flex;
  justify-content: space-between;
  margin-bottom: ${o => (o.$noBottomMargin ? 0 : o.theme.rowGap.extraSmall)};
`

export const ColorGroupSwatchRow = styled(VariantSelectionOptionsRow)`
  margin-bottom: ${o => o.theme.rowGap.extraSmall};
  gap: ${o => o.theme.rowGap.tiny};
  &:last-child {
    margin-bottom: 0;
  }
`

const StyledFormGroupLabelWrapper = styled.div`
  margin-bottom: 0;
`
